import type { Schedule } from "./types";

export type ShareMode = "snapshot" | "publish";
export interface ShareCreated { id: string; mode: ShareMode; url: string; token?: string }
export interface Published { id: string; token: string; url: string }
export interface SharedSchedule { id: string; mode: ShareMode; schedule: Schedule; updated: string }
export type SuggestValue = string | number | boolean | null;
export interface SuggestInput {
  target_type: "course" | "session" | "person";
  /** Course id, session key, or person key. */
  target_id: string;
  /** Unit code; required for persons, otherwise inferred from the "<UNIT>-" prefix. */
  unit?: string;
  field: string;
  /** The exact current value shown in the UI (the override only applies while the source still says this). */
  old: SuggestValue;
  new: SuggestValue;
  note?: string;
  contact?: string;
  turnstileToken?: string;
}
/** `snippet` is HTML-escaped text with matches wrapped in <mark>; `name` is đ-folded (prefer the core course name). */
export interface SearchHit { course_id: string; fac: string; name: string; snippet: string }

export class ApiError extends Error {
  constructor(public status: number, message: string) { super(message); }
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const r = await fetch(path, { ...init, headers: { ...(init?.body ? { "content-type": "application/json" } : {}), ...init?.headers } });
  const j = (await r.json().catch(() => ({}))) as T & { error?: string };
  if (!r.ok) throw new ApiError(r.status, j.error || `HTTP ${r.status}`);
  return j;
}

const post = <T>(path: string, body: unknown, method = "POST") => call<T>(path, { method, body: JSON.stringify(body) });

/** Immutable link; identical schedules get the same id. */
export const shareSnapshot = (schedule: Schedule) => post<ShareCreated>("/api/share", { schedule, mode: "snapshot" });

/** Mutable link. The returned token is shown only once: keep it in localStorage to update later. */
export const publishSchedule = (schedule: Schedule) => post<Published>("/api/share", { schedule, mode: "publish" }) as Promise<Published>;

export const updatePublished = (id: string, token: string, schedule: Schedule) =>
  post<{ id: string; mode: "publish"; url: string; updated: string }>(`/api/share/${encodeURIComponent(id)}`, { schedule, token }, "PUT");

export const loadShared = (id: string) => call<SharedSchedule>(`/api/share/${encodeURIComponent(id)}`);

export const suggest = (s: SuggestInput) => post<{ ok: true; id: number }>("/api/suggest", s);

export async function search(q: string, opts: { fac?: string; signal?: AbortSignal } = {}): Promise<SearchHit[]> {
  if (!q.trim()) return [];
  const p = new URLSearchParams({ q });
  if (opts.fac) p.set("fac", opts.fac);
  return (await call<{ results: SearchHit[] }>(`/api/search?${p}`, { signal: opts.signal })).results;
}

/** Live calendar feed URL; pass `webcal: true` for a subscribe link. */
export function calUrl(id: string, opts: { term?: "winter" | "summer"; webcal?: boolean; origin?: string } = {}): string {
  const origin = opts.origin ?? (typeof location !== "undefined" ? location.origin : "");
  const u = `${origin}/api/cal/${encodeURIComponent(id)}.ics${opts.term ? `?term=${opts.term}` : ""}`;
  return opts.webcal ? u.replace(/^https?:/, "webcal:") : u;
}

export const googleCalUrl = (id: string, opts: { term?: "winter" | "summer"; origin?: string } = {}) =>
  `https://calendar.google.com/calendar/r?cid=${encodeURIComponent(calUrl(id, { ...opts, webcal: true }))}`;

export const getConfig = () => call<{ turnstileSiteKey: string | null }>("/api/config");
